import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  createGuestJourneyBotAiChatCompletion,
  createJourneyBotAiChatCompletion,
} from './api/journeyBotApi';
import { useAuth } from './AuthContext';
import { useData } from './DataProviderContext';
import { GUEST_JOURNEY_LIMITS, SIGNED_IN_JOURNEY_LIMITS } from './config/journeyLimits';
import {
  clearGuestJourneyState,
  createGuestJourneyState,
  dismissGuestAiNotice,
  isGuestAiNoticeDismissed,
  guestJourneyToBoardData,
  loadGuestJourneyState,
  saveGuestJourneyState,
} from './guestJourneySession';
import { countGoalFocusWords, GOAL_FOCUS_MAX_WORDS } from './storage/actionItemModel';
import SignedInJourneyAiChatDialog from './SignedInJourneyAiChatDialog';

const WELCOME = 'Let’s turn what you want to achieve into a journey. What goal would you like to work toward?';

const JOURNEY_CHAT_SYSTEM_PROMPT = `You are JourneyBot's guided journey builder inside the app.

Help the user create one focused, useful goal journey. Be supportive, journey-type neutral, and concise. Ask at most one focused question per response. Learn the goal, current situation, desired outcome, constraints, decisions, blockers, useful steps, and practical actions. Do not claim anything has been saved. Do not request sensitive personal information.

During discovery, respond in plain language and do not output JSON. A later instruction will request the structured journey.`;

const journeyDraftInstruction = `Create the focused JourneyBot journey now from this conversation.

Return only one valid JSON object without Markdown or commentary with exactly: goal, startState, context, endState, steps.
- goal is a clear title of ${GOAL_FOCUS_MAX_WORDS} words or fewer. Create exactly one goal.
- context is 300 characters or fewer.
- steps is an array of no more than ${GUEST_JOURNEY_LIMITS.stepsPerGoal} useful ordered steps. Use fewer when the context does not need the maximum.
- every step has exactly: id, title, description, actions.
- every actions array has no more than ${GUEST_JOURNEY_LIMITS.actionsPerStep} concise practical actions. Normally use ${SIGNED_IN_JOURNEY_LIMITS.targetActionsPerStep.minimum} to ${SIGNED_IN_JOURNEY_LIMITS.targetActionsPerStep.maximum}, and fewer when appropriate.
- every action has exactly: id, title, description.
- all ids are unique and use only letters, numbers, underscores, or hyphens.
- do not invent personal facts; use empty strings where information was not established.`;

const safeString = (value, maximum = 10_000) => (typeof value === 'string' ? value.trim().slice(0, maximum) : '');
const safeId = (value, fallback) => safeString(value, 128).replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || fallback;

export const parseJourneyDraft = (content) => {
  const text = String(content || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');
  if (firstBrace < 0 || lastBrace <= firstBrace) throw new Error('The AI did not return a journey map. Try again.');

  let candidate;
  try {
    candidate = JSON.parse(text.slice(firstBrace, lastBrace + 1));
  } catch {
    throw new Error('The AI returned an incomplete journey map. Try again.');
  }

  const goal = safeString(candidate?.goal, 200);
  if (!goal) throw new Error('The journey map needs one goal. Continue the conversation and try again.');
  if (countGoalFocusWords(goal) > GOAL_FOCUS_MAX_WORDS) throw new Error(`The journey goal must be ${GOAL_FOCUS_MAX_WORDS} words or fewer. Try again.`);
  if (!Array.isArray(candidate?.steps) || candidate.steps.length === 0) throw new Error('The journey map needs at least one step. Try again.');

  const usedIds = new Set();
  const uniqueId = (value, fallback) => {
    const base = safeId(value, fallback);
    let next = base;
    let suffix = 2;
    while (usedIds.has(next)) next = `${base.slice(0, 120)}-${suffix++}`;
    usedIds.add(next);
    return next;
  };

  const steps = candidate.steps.slice(0, GUEST_JOURNEY_LIMITS.stepsPerGoal).map((step, stepIndex) => {
    const title = safeString(step?.title, 500);
    if (!title) throw new Error(`Step ${stepIndex + 1} needs a title. Try again.`);
    const rawActions = Array.isArray(step?.actions) ? step.actions : [];
    return {
      id: uniqueId(step?.id, `step-${stepIndex + 1}`),
      title,
      description: safeString(step?.description),
      actions: rawActions.slice(0, GUEST_JOURNEY_LIMITS.actionsPerStep).map((action, actionIndex) => {
        const actionTitle = safeString(action?.title, 500);
        if (!actionTitle) throw new Error(`Action ${actionIndex + 1} in step ${stepIndex + 1} needs a title. Try again.`);
        return {
          id: uniqueId(action?.id, `step-${stepIndex + 1}-action-${actionIndex + 1}`),
          title: actionTitle,
          description: safeString(action?.description),
        };
      }),
    };
  });

  return {
    goal,
    startState: safeString(candidate?.startState, 500),
    context: safeString(candidate?.context, 300),
    endState: safeString(candidate?.endState, 500),
    steps,
  };
};

const apiConversation = (messages, finalInstruction) => [
  { role: 'system', content: JOURNEY_CHAT_SYSTEM_PROMPT },
  ...messages.slice(-18).map(({ role, content }) => ({ role, content })),
  ...(finalInstruction ? [{ role: 'user', content: finalInstruction }] : []),
];

function GuestJourneyAiChatDialog({ open, onClose, onGenerated }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { authState } = useAuth();
  const { loadGuestPreview } = useData();
  const isSignedIn = Boolean(authState.user);
  const [guestState, setGuestState] = useState(() => loadGuestJourneyState() || createGuestJourneyState());
  const [showNotice, setShowNotice] = useState(() => !isGuestAiNoticeDismissed());
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [busy, setBusy] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const restored = loadGuestJourneyState();
    if (restored) setGuestState(restored);
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [guestState.messages, busy]);

  const persist = (changes) => {
    const next = saveGuestJourneyState({ ...guestState, ...changes });
    setGuestState(next);
    return next;
  };

  const complete = (messages, options) => (
    isSignedIn
      ? createJourneyBotAiChatCompletion(messages)
      : createGuestJourneyBotAiChatCompletion(messages, { attemptId: guestState.attemptId, ...options })
  );

  const hasUserMessage = useMemo(() => guestState.messages.some((message) => message.role === 'user'), [guestState.messages]);
  const guestDiscoveryComplete = !isSignedIn && guestState.successfulAssistantResponses >= GUEST_JOURNEY_LIMITS.discoveryResponsesPerAttempt;
  const transcriptLocked = guestState.phase === 'map' && !isSignedIn;

  const createJourney = async (messages = guestState.messages) => {
    if (!messages.some((message) => message.role === 'user') || busy) return;
    setBusy('generation');
    setStatus({ type: 'idle', message: '' });
    try {
      const result = await complete(apiConversation(messages, journeyDraftInstruction), { responseKind: 'generation' });
      const journey = parseJourneyDraft(result.assistant.content);
      const next = persist({ phase: 'map', journey, boardData: guestJourneyToBoardData(journey), generationError: '', input: '' });
      loadGuestPreview?.(journey, next);
      onGenerated?.();
    } catch (error) {
      const message = error.status === 429
        ? 'AI is temporarily busy. Please try again later.'
        : error.message || 'The journey map could not be created.';
      persist({ generationError: message });
      setStatus({ type: 'error', message });
    } finally {
      setBusy('');
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const content = guestState.input.trim();
    if (!content || busy || transcriptLocked || guestDiscoveryComplete) return;
    const userMessage = { id: `user-${Date.now()}`, role: 'user', content };
    const messages = [...guestState.messages, userMessage];
    persist({ messages, input: '', generationError: '' });
    setBusy('discovery');
    setStatus({ type: 'idle', message: '' });
    try {
      const result = await complete(apiConversation(messages), { responseKind: 'discovery' });
      const assistant = { id: `assistant-${Date.now()}`, role: 'assistant', content: result.assistant.content };
      const nextMessages = [...messages, assistant];
      const successfulAssistantResponses = isSignedIn
        ? guestState.successfulAssistantResponses
        : guestState.successfulAssistantResponses + 1;
      persist({ messages: nextMessages, successfulAssistantResponses });
      if (!isSignedIn && successfulAssistantResponses === GUEST_JOURNEY_LIMITS.discoveryResponsesPerAttempt) {
        setBusy('');
        await createJourney(nextMessages);
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.status === 429 ? 'AI is temporarily busy. Please try again later.' : error.message || 'The AI chat is unavailable right now.',
      });
    } finally {
      setBusy('');
    }
  };

  const resetAttempt = () => {
    clearGuestJourneyState();
    const next = saveGuestJourneyState(createGuestJourneyState());
    setGuestState(next);
    setStatus({ type: 'idle', message: '' });
    setResetOpen(false);
  };

  const closeDialog = () => { if (!busy) onClose(); };

  return (
    <>
      <Dialog open={open} onClose={closeDialog} fullScreen={fullScreen} fullWidth maxWidth="md" aria-labelledby="journey-ai-chat-title">
        <DialogTitle id="journey-ai-chat-title" sx={{ pr: 7 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" component="span" sx={{ fontWeight: 800 }}>Create a journey with AI</Typography>
              <Typography variant="body2" color="text.secondary">Chat toward one focused goal journey</Typography>
            </Box>
          </Stack>
          <IconButton aria-label="Close AI journey chat" onClick={closeDialog} disabled={Boolean(busy)} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Box aria-live="polite" sx={{ minHeight: 240, maxHeight: { xs: 'none', sm: 380 }, overflowY: 'auto', px: 0.5 }}>
              <Stack spacing={1.5}>
                {[{ id: 'welcome', role: 'assistant', content: WELCOME }, ...guestState.messages].map((message) => (
                  <Paper key={message.id} variant="outlined" sx={{ p: 1.5, maxWidth: '88%', alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start', bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper', color: message.role === 'user' ? 'primary.contrastText' : 'text.primary', whiteSpace: 'pre-wrap' }}>
                    <Typography variant="body2">{message.content}</Typography>
                  </Paper>
                ))}
                {busy === 'discovery' && <CircularProgress size={22} aria-label="AI is responding" />}
                <div ref={endRef} />
              </Stack>
            </Box>

            {showNotice && !isSignedIn && (
              <Alert severity="info" onClose={() => { dismissGuestAiNotice(); setShowNotice(false); }} data-testid="guest-ai-notice">
                Messages are processed by AI. This conversation and journey are temporary and are not permanently saved until you sign in and save. Do not enter sensitive personal information.
              </Alert>
            )}

            {transcriptLocked && <Alert severity="info">This creation transcript is view-only. Start over from the journey preview to create a new guest journey.</Alert>}

            <Box component="form" onSubmit={sendMessage}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <TextField label="Message JourneyBot" value={guestState.input} onChange={(event) => persist({ input: event.target.value })} multiline maxRows={5} fullWidth disabled={Boolean(busy) || transcriptLocked || guestDiscoveryComplete} inputProps={{ maxLength: 2000 }} />
                <Button type="submit" variant="contained" endIcon={<SendOutlinedIcon />} disabled={!guestState.input.trim() || Boolean(busy) || transcriptLocked || guestDiscoveryComplete} sx={{ minHeight: 56, width: { xs: '100%', sm: 'auto' } }}>Send</Button>
              </Stack>
            </Box>

            {status.type === 'error' && <Alert severity="error">{status.message}</Alert>}
            {guestState.generationError && (
              <Button variant="contained" onClick={() => createJourney()} disabled={Boolean(busy)} startIcon={busy === 'generation' ? <CircularProgress size={18} /> : <AutoAwesomeOutlinedIcon />} data-testid="retry-journey-generation">Try again</Button>
            )}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button onClick={closeDialog} disabled={Boolean(busy)} sx={{ width: { xs: '100%', sm: 'auto' } }}>Close</Button>
          {guestState.phase === 'chat' && (
            <Button color="error" variant="outlined" onClick={() => setResetOpen(true)} disabled={Boolean(busy)} startIcon={<DeleteOutlineIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>Reset guest journey</Button>
          )}
          {(!transcriptLocked || guestState.phase === 'chat') && (
            <Button variant="contained" onClick={() => createJourney()} disabled={!hasUserMessage || Boolean(busy)} startIcon={busy === 'generation' ? <CircularProgress size={18} /> : <AutoAwesomeOutlinedIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>Create journey now</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} aria-labelledby="reset-guest-title">
        <DialogTitle id="reset-guest-title">Reset guest journey?</DialogTitle>
        <DialogContent><Typography>The temporary conversation and any generated journey will be permanently cleared.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Keep journey</Button>
          <Button color="error" variant="contained" onClick={resetAttempt}>Reset guest journey</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function JourneyAiChatDialog(props) {
  const { authState } = useAuth();
  const hasGuestSession = Boolean(loadGuestJourneyState());

  if (authState.user && !hasGuestSession) {
    return <SignedInJourneyAiChatDialog {...props} />;
  }

  return <GuestJourneyAiChatDialog {...props} />;
}
